import React from "react";
import { dateLocale } from "@/lib/dateTime";
import { formatINR } from "@/lib/utils";
import { AGREEMENT_TYPE_LABELS, type AgreementRender } from "@/types/agreement";

const fmtDate = (d?: string | null) =>
  d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-";

/**
 * An agreement laid out as its signers read it. Clause HTML comes from the server, where it is sanitized and the
 * merged values are escaped, so it is rendered as HTML here.
 */
export interface LetterheadView {
  src: string;
  /** Room the artwork takes at the top and bottom of page 1, in millimetres of an A4 page */
  top: number;
  bottom: number;
}

export function AgreementDocument({ doc, logoSrc, letterhead }: { doc: AgreementRender; logoSrc?: string; letterhead?: LetterheadView | null }) {
  // On the company letterhead: the top and bottom of its first page frame the document, and the name, address and logo (which it carries) are left out
  const lh: LetterheadView | null = letterhead ?? (doc.letterhead ? { src: `/api/Agreement/Letterhead/${doc.letterhead.id}`, top: doc.letterhead.top, bottom: doc.letterhead.bottom } : null);
  const [logoFailed, setLogoFailed] = React.useState(false);
  let n = 0;
  const sections = doc.sections.filter((s) => s.type !== "cover");
  const cover = doc.sections.find((s) => s.type === "cover");
  const subtitle = cover && cover.title && cover.title !== "Agreement" ? cover.title : "";
  return (
    <article className="bg-white text-foreground rounded-xl shadow-sm border max-w-3xl mx-auto text-sm leading-relaxed overflow-hidden shrink-0">
      {lh && lh.top > 0 && <div aria-hidden style={{ aspectRatio: `210 / ${lh.top}`, backgroundImage: `url(${lh.src})`, backgroundSize: "100% auto", backgroundPosition: "top", backgroundRepeat: "no-repeat" }} />}
      <header className="px-8 sm:px-12 pt-8 pb-5 border-b">
        {!lh && (
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-base font-bold text-primary">{doc.company.name}</div>
              <div className="text-[11px] text-muted-foreground">{[doc.company.address, doc.company.phone, doc.company.email].filter(Boolean).join("  ·  ")}</div>
              {doc.company.gstin && <div className="text-[11px] text-muted-foreground">GSTIN {doc.company.gstin}</div>}
            </div>
            {logoSrc && !logoFailed && <img src={logoSrc} alt={doc.company.name} onError={() => setLogoFailed(true)} className="h-12 max-w-[160px] object-contain" />}
          </div>
        )}
        <h1 className={`${lh ? "" : "mt-5 "}text-center text-2xl font-bold uppercase tracking-wide text-primary`}>{doc.title}</h1>
        {subtitle && <div className="mt-1 text-center text-xs italic text-muted-foreground">{subtitle}</div>}
        <div className="h-0.5 bg-primary mt-3 mb-2" />
        <div className="text-center text-[11px] text-muted-foreground">
          {doc.display_number} · Effective {fmtDate(doc.effective_date)} · Ends {fmtDate(doc.end_date)}
        </div>
      </header>

      <div className="px-8 sm:px-12 py-6 space-y-6">
        {sections.map((s) => {
          const numbered = s.numbered !== false;
          if (numbered) n++;
          return (
            <section key={s.id} data-section-id={s.id}>
              {s.show_title !== false && (
                <h2 className={`text-base font-bold text-primary mb-2 ${s.type === "acceptance" && !numbered ? "text-center" : ""}`}>
                  {numbered ? `${n}. ` : ""}
                  {s.title}
                </h2>
              )}
              {s.type === "facts" && <Facts s={s} />}
              {s.html && <div className="prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: s.html }} />}
              {s.type === "estimate" && doc.commercials && <PriceTable c={doc.commercials} />}
              {s.type === "payment" && doc.payment?.length ? (
                <table className="mt-2 w-full text-sm border">
                  <tbody>
                    {doc.payment.map((p, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-3 py-1.5">{p.label}</td>
                        <td className="px-3 py-1.5 text-right">{p.percent}%</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{formatINR(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
              {s.type === "acceptance" && <Signatures doc={doc} />}
            </section>
          );
        })}
      </div>
      {lh && lh.bottom > 0 && <div aria-hidden style={{ aspectRatio: `210 / ${lh.bottom}`, backgroundImage: `url(${lh.src})`, backgroundSize: "100% auto", backgroundPosition: "bottom", backgroundRepeat: "no-repeat" }} />}
    </article>
  );
}

/** Label / value rows; with the "columns" style each group is a column under its own heading (the parties box) */
function Facts({ s }: { s: AgreementRender["sections"][number] }) {
  const facts = s.facts || [];
  if (s.variant === "columns") {
    const groups = [...new Set(facts.map((f) => f.group || ""))];
    return (
      <div className="grid gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-2">
        {groups.map((g) => (
          <div key={g} className="bg-white p-3 text-[13px] space-y-0.5">
            {g && <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-primary">{g}</div>}
            {facts.filter((f) => (f.group || "") === g).map((f, i) => (
              <div key={i}>
                <span className="text-muted-foreground">{f.label}: </span>
                <span className="whitespace-pre-line break-words">{f.value || "-"}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  return (
    <table className="w-full border text-[13px]">
      <tbody>
        {facts.map((f, i) => (
          <tr key={i} className="border-b last:border-0 align-top">
            <th className="w-[30%] bg-primary/5 px-3 py-1.5 text-left font-semibold">{f.label}</th>
            <td className="px-3 py-1.5 whitespace-pre-line break-words">{f.value || "-"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PriceTable({ c }: { c: NonNullable<AgreementRender["commercials"]> }) {
  if (!c.lines.length) return <p className="italic text-muted-foreground">Prices are as agreed in the accepted quotation.</p>;
  return (
    <div className="mt-2 overflow-x-auto">
      <table className="w-full text-sm border">
        <thead>
          <tr className="bg-primary text-white text-left">
            <th className="px-3 py-1.5 font-semibold">Description</th>
            <th className="px-3 py-1.5 font-semibold text-right">Qty</th>
            <th className="px-3 py-1.5 font-semibold text-right">Unit price</th>
            <th className="px-3 py-1.5 font-semibold text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {c.lines.map((l, i) => (
            <tr key={i} className="border-b">
              <td className="px-3 py-1.5">
                {l.description}
                {l.spec && <div className="text-[11px] text-muted-foreground">{l.spec}</div>}
              </td>
              <td className="px-3 py-1.5 text-right whitespace-nowrap">{l.quantity}{l.unit ? ` ${l.unit}` : ""}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{formatINR(l.unit_price)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{formatINR(l.line_total)}</td>
            </tr>
          ))}
          <tr className="bg-muted/50"><td colSpan={3} className="px-3 py-1.5 text-right font-semibold">Subtotal</td><td className="px-3 py-1.5 text-right tabular-nums">{formatINR(c.subtotal)}</td></tr>
          <tr className="bg-muted/50"><td colSpan={3} className="px-3 py-1.5 text-right font-semibold">GST</td><td className="px-3 py-1.5 text-right tabular-nums">{formatINR(c.tax_total)}</td></tr>
          <tr className="bg-success-soft"><td colSpan={3} className="px-3 py-1.5 text-right font-bold">Total (inclusive of GST)</td><td className="px-3 py-1.5 text-right font-bold tabular-nums">{formatINR(c.grand_total)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function Signatures({ doc }: { doc: AgreementRender }) {
  const box = (heading: string, party: "CUSTOMER" | "COMPANY", fallback: string) => {
    const people = doc.signers.filter((s) => s.party === party);
    return (
      <div className="flex-1 min-w-[200px]">
        <div className="font-semibold text-primary mb-2">{heading}</div>
        {(people.length ? people : [{ name: fallback, title: null, status: "PENDING", signed_at: null }]).map((s, i) => (
          <div key={i} className="mb-3">
            <div className="h-10 flex items-end text-lg italic font-semibold text-foreground">{s.status === "SIGNED" ? s.name : ""}</div>
            <div className="border-t border-border pt-1 font-semibold">{s.name}</div>
            {s.title && <div className="text-xs text-muted-foreground">{s.title}</div>}
            <div className="text-[11px] text-success">{s.status === "SIGNED" && s.signed_at ? `Signed ${fmtDate(s.signed_at)}` : s.status === "DECLINED" ? "Declined" : ""}</div>
          </div>
        ))}
      </div>
    );
  };
  return (
    <div className="mt-3 flex flex-wrap gap-8">
      {box(`For ${doc.company.name || "the Supplier"}`, "COMPANY", "Authorised signatory")}
      {box(`For ${doc.customer.company || "the Customer"}`, "CUSTOMER", doc.customer.contact || "Authorised signatory")}
    </div>
  );
}
